import { useEffect, useRef, useState } from 'react';
import { MicrophoneOptions } from '../type';
import { ChipStatus } from '../../../../shared/ppr-chip-status/ppr-chip-status';
import { useInterval } from '../../../../../hooks/use-interval';
import { sleep } from '../../../../util/util';

export const useMicrophoneCheck = (microphoneOptions: MicrophoneOptions) => {
  // Properly type streamRef as a reference to a MediaStream or null
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const failTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [hasSound, setHasSound] = useState<boolean>();
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

  // State for the selected microphone and list of available microphones
  const [selectedMic, setSelectedMic] = useState<MediaDeviceInfo>();
  const refSelectedMic = useRef(selectedMic);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [microphoneStatus, setMicrophoneStatus] = useState<ChipStatus>('not-started');
  const [permissionState, setPermissionState] = useState<string>('default');
  const [loading, setLoading] = useState<boolean>(false);
  const hasMicrophoneConnected = useRef<boolean>(false);

  // State interval timeout
  const [seconds, setSeconds] = useState(5);
  const intervalSeconds = useInterval(() => setSeconds((s) => s - 1), 1000);

  const getMicrophones = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();

    // Filter devices for audio inputs
    const audioInputs = devices.filter((device) => device.deviceId !== '' && device.kind === 'audioinput');
    setMicrophones(audioInputs);

    if (audioInputs.length === 0) setMicrophoneStatus('failed');

    // Set the first available microphone if available
    if (audioInputs.length > 0) {
      setSelectedMic(audioInputs[0]);
    }
  };

  const analyzeFiveSecondAudio = async (audioBlob: Blob) => {
    try {
      const silenceThreshold = microphoneOptions.silenceThreshold;
      // Analyze the Blob for silence
      const isSilent = await analyzeAudioBlobForSilence(audioBlob, silenceThreshold);
      await sleep(3000);

      if (!hasMicrophoneConnected.current) {
        setMicrophoneStatus('failed');
        initializeState();
        return;
      }

      // Output the result
      setHasSound(!isSilent);
      if (isSilent) {
        setMicrophoneStatus('failed');
        setLoading(false);
      } else {
        setMicrophoneStatus('passed');
        setIsRecording(false);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error analyzing the audio blob:', error);
      setMicrophoneStatus('failed');
      initializeState();
    }
  };

  const analyzeAudioBlobForSilence = async (
    audioBlob: Blob,
    silenceThreshold: number = 0.01 // Threshold for silence
  ): Promise<boolean> => {
    try {
      // Step 1: Create an AudioContext
      const audioContext = new AudioContext();
      // Step 2: Convert Blob to ArrayBuffer
      const arrayBuffer = await audioBlob.arrayBuffer();
      // Step 3: Decode Audio Data into an AudioBuffer
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      // Step 4: Analyze Audio Data
      const { length, numberOfChannels } = audioBuffer;
      let totalAmplitude = 0;
      let sampleCount = 0;

      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        // Calculate the absolute amplitude for each sample
        for (let i = 0; i < length; i++) {
          totalAmplitude += Math.abs(channelData[i]);
          sampleCount++;
        }
      }
      // Step 5: Calculate the Average Amplitude
      const averageAmplitude = totalAmplitude / sampleCount;
      // Step 6: Determine if Silent
      const isSilent = averageAmplitude < silenceThreshold;
      // Cleanup and Resolve
      audioContext.close();

      return isSilent;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // UseEffect to fetch microphones on component mount and clean up the media stream on unmount
  useEffect(() => {
    if (permissionState !== 'denied') getMicrophones();

    // Event listener for device changes
    navigator.mediaDevices.ondevicechange = () => {
      setMicrophoneStatus('not-started');
      audioChunksRef.current = [];
      initializeState();
      getMicrophones();
    };

    return () => {
      // Cleanup the media stream tracks when the component is unmounted
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      navigator.mediaDevices.ondevicechange = null;
    };
  }, [permissionState]);

  useEffect(() => {
    if (seconds === 0) {
      handleStopRecorder();
      intervalSeconds.stop();
    }
  }, [seconds]);

  // UseEffect to initializeState when select another device
  useEffect(() => {
    if (failTimeoutRef.current) {
      clearTimeout(failTimeoutRef.current);
      failTimeoutRef.current = null;
    }

    if (!selectedMic) {
      if (permissionState !== 'default') {
        failTimeoutRef.current = setTimeout(() => {
          setMicrophoneStatus('failed');
        }, 1000);
      }
    } else {
      refSelectedMic.current = selectedMic;
      setMicrophoneStatus('not-started');
    }

    return () => {
      if (failTimeoutRef.current) {
        clearTimeout(failTimeoutRef.current);
        failTimeoutRef.current = null;
      }
    };
  }, [selectedMic]);

  useEffect(() => {
    let autoCheck = false;
    const interval = setInterval(async () => {
      try {
        const audioConstraints: boolean | MediaTrackConstraints = refSelectedMic.current?.deviceId
          ? ({ deviceId: { exact: refSelectedMic.current.deviceId } } as MediaTrackConstraints)
          : true;

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        });
        stream.getTracks().forEach((track) => track.stop());

        hasMicrophoneConnected.current = true;

        if (!autoCheck) {
          await getMicrophones();
          autoCheck = true;
        }
      } catch (error) {
        hasMicrophoneConnected.current = false;

        const resetMicrophoneInfo = () => {
          autoCheck = false;
          setSelectedMic(undefined);
          setMicrophones([]);
          setHasSound(undefined)
        };

        if ((error instanceof Error || error instanceof DOMException) && error.message !== 'Permission denied') {
          resetMicrophoneInfo();
          console.error('Failed to access microphone', error);
        } else {
          setHasSound(undefined)
          console.error('Unknown error', error);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // UseEffect to detecting the silent audioBlob
  useEffect(() => {
    if (audioBlob) analyzeFiveSecondAudio(audioBlob);
  }, [audioBlob]);

  const handleStopRecorder = () => {
    if (!mediaRecorder) return;
    mediaRecorder.stop();
    mediaRecorder.stream.getTracks().forEach((track) => track.stop()); // Stop the microphone stream
    setMediaStream(null);
  };

  // Reset initialize value when interrupt permissionState, access device
  const initializeState = () => {
    handleStopRecorder();
    intervalSeconds.stop();
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onstop = null;
    }
    setIsRecording(false);
    setHasSound(undefined);
    setLoading(false);
    setMediaStream(null);
    setAudioBlob(null);
  };

  const handleStartRecording = async (targetEleRef: React.RefObject<HTMLElement>) => {
    if (!selectedMic?.deviceId) return;
    try {
      targetEleRef?.current?.focus();
      setSeconds(5);
      intervalSeconds.start(); // Start count down
      setAudioBlob(null); // Reset previous recording
      setLoading(true); // Disabled the selected device
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selectedMic.deviceId } },
      });
      setMediaStream(audioStream);
      setMicrophoneStatus('not-started'); // Set status analyzed

      const recorder = new MediaRecorder(audioStream);
      setMediaRecorder(recorder);
      audioChunksRef.current = []; // Clear previous chunks

      recorder.ondataavailable = (event) => {
        setMicrophoneStatus('analyzing');
        audioChunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });
        // Save the Blob for the AudioVisualizer
        setAudioBlob(audioBlob);
        setIsRecording(false);
      };

      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);

      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        console.error('Failed to access microphone', error);
      } else if (error instanceof OverconstrainedError) {
        console.error('Failed to find microphone', error);
      } else {
        console.error('An unexpected error occurred:', error);
      }
      initializeState();
      setMicrophoneStatus('failed');
    }
  };

  const handlePermissionChange = async () => {
    try {
      const permissionsStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      setPermissionState(permissionsStatus.state);
      permissionsStatus.onchange = () => {
        setPermissionState(permissionsStatus.state);
        if (permissionsStatus.state === 'denied') {
          setSelectedMic(undefined);
          setMicrophoneStatus('failed');
          initializeState();
          setMicrophones([]);
        } else if (permissionsStatus.state === 'granted') {
          setMicrophoneStatus('not-started');
        }
      };
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      setMicrophoneStatus('failed');
    } finally {
      initializeState();
    }
  };

  useEffect(() => {
    handlePermissionChange();
  }, [permissionState]);

  return {
    loading,
    permissionState,
    microphones,
    selectedMic,
    setSelectedMic,
    microphoneStatus,
    setMicrophoneStatus,
    handleStartRecording,
    mediaStream,
    mediaRecorder,
    isRecording,
    audioBlob,
    countDown: seconds,
    hasSound,
  };
};
